import { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell
} from "recharts";
import { Heart, Shield, Globe, Palette, DollarSign, AlertTriangle, TrendingUp } from "lucide-react";
import { API } from "../../lib/constants";

const CARD_STYLE = "bg-card border border-border rounded-xl p-6";
const CHART_TOOLTIP = { background: "#0f172a", border: "1px solid #334155", borderRadius: "8px" };
const AXIS_TICK = { fill: "#94a3b8", fontSize: 11 };

const ExtractionEconomySection = ({ data }) => {
  if (!data) return null;
  const disc = data.discoveries?.find(d => d.id === "extraction_proof");
  if (!disc) return null;
  const barData = disc.data.top_countries?.map(c => ({ name: c.country, count: c.count })) || [];

  return (
    <div className={CARD_STYLE} data-testid="research-extraction">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 rounded-lg bg-amber-500/20">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">The Extraction Economy</h3>
          <p className="text-sm text-muted-foreground">Who builds extraction-oriented AI?</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
            <p className="text-sm text-amber-300">{disc.insight}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-black/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-400">{disc.data.total_extraction}</p>
              <p className="text-xs text-muted-foreground">Total Extraction Apps</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-pink-400">{disc.stat}</p>
              <p className="text-xs text-muted-foreground">Female-Founded</p>
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={barData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="name" tick={AXIS_TICK} interval={0} />
            <YAxis tick={AXIS_TICK} />
            <Tooltip contentStyle={CHART_TOOLTIP} />
            <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]}>
              {barData.map((entry, i) => (
                <Cell key={entry.name || `bar-${i}`} fill={i === 0 ? "#f59e0b" : "#78716c"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const GovernanceGenderSection = ({ data }) => {
  if (!data) return null;
  const disc = data.discoveries?.find(d => d.id === "system_aware");
  if (!disc) return null;
  const govData = disc.data;
  const chartData = Object.entries(govData).map(([f, vals]) => ({
    name: f === "female_only" ? "Female" : f === "male_only" ? "Male" : "Mixed",
    "System-Aware": vals.system_aware || 0,
    "Neutral": vals.neutral || 0,
    "Market-First": vals.market_first || 0,
  }));

  return (
    <div className={CARD_STYLE} data-testid="research-governance-gender">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 rounded-lg bg-violet-500/20">
          <Shield className="w-5 h-5 text-violet-400" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">Governance Awareness by Gender</h3>
          <p className="text-sm text-muted-foreground">Who thinks about systemic impact?</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="p-4 bg-violet-500/10 rounded-lg border border-violet-500/20">
            <p className="text-sm text-violet-300">{disc.insight}</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {chartData.map(d => (
              <div key={d.name} className="bg-black/20 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-violet-400">{d["System-Aware"]}%</p>
                <p className="text-xs text-muted-foreground">{d.name} system-aware</p>
              </div>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="name" tick={AXIS_TICK} interval={0} />
            <YAxis tick={AXIS_TICK} unit="%" />
            <Tooltip contentStyle={CHART_TOOLTIP} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="System-Aware" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
            <Bar dataKey="Neutral" fill="#64748b" radius={[3, 3, 0, 0]} />
            <Bar dataKey="Market-First" fill="#ef4444" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const AfricaCareSection = ({ data }) => {
  if (!data) return null;
  const disc = data.discoveries?.find(d => d.id === "africa_care_first");
  const ranking = data.regional_care_ranking || [];
  if (!disc) return null;

  const chartData = ranking.slice(0, 8).map(r => ({
    name: r.country,
    Care: r.care_pct,
    Utility: r.utility_pct,
  }));

  return (
    <div className={CARD_STYLE} data-testid="research-africa-care">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 rounded-lg bg-emerald-500/20">
          <Globe className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">The Care-First Continents</h3>
          <p className="text-sm text-muted-foreground">Where does care orientation outpace utility?</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
            <p className="text-sm text-emerald-300">{disc.insight}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-black/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-emerald-400">{disc.stat}</p>
              <p className="text-xs text-muted-foreground">Africa Care-Oriented</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-400">{ranking[0]?.care_utility_ratio || "?"}x</p>
              <p className="text-xs text-muted-foreground">{ranking[0]?.country} Care:Util</p>
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} layout="vertical" barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis type="number" tick={AXIS_TICK} unit="%" />
            <YAxis type="category" dataKey="name" tick={AXIS_TICK} width={80} />
            <Tooltip contentStyle={CHART_TOOLTIP} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Care" fill="#10b981" radius={[0, 3, 3, 0]} />
            <Bar dataKey="Utility" fill="#3b82f6" radius={[0, 3, 3, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const MonetizationGenderSection = ({ data }) => {
  if (!data) return null;
  const disc = data.discoveries?.find(d => d.id === "free_vs_paid");
  if (!disc) return null;
  const monData = disc.data;
  const chartData = ["free", "enterprise", "subscription", "freemium"].map(m => ({
    name: m.charAt(0).toUpperCase() + m.slice(1),
    "Female": monData.female_only?.[m] || 0,
    "Male": monData.male_only?.[m] || 0,
    "Mixed": monData.mixed?.[m] || 0,
  }));

  return (
    <div className={CARD_STYLE} data-testid="research-monetization-gender">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 rounded-lg bg-cyan-500/20">
          <DollarSign className="w-5 h-5 text-cyan-400" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">The Free vs Paid Divide</h3>
          <p className="text-sm text-muted-foreground">How founder gender shapes monetization</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
            <p className="text-sm text-cyan-300">{disc.insight}</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="name" tick={AXIS_TICK} interval={0} />
            <YAxis tick={AXIS_TICK} unit="%" />
            <Tooltip contentStyle={CHART_TOOLTIP} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Female" fill="#ec4899" radius={[3, 3, 0, 0]} />
            <Bar dataKey="Male" fill="#3b82f6" radius={[3, 3, 0, 0]} />
            <Bar dataKey="Mixed" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const DiscoveryAnalyses = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/discovery-insights`).then(res => {
      setData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  return (
    <>
      <ExtractionEconomySection data={data} />
      <GovernanceGenderSection data={data} />
      <AfricaCareSection data={data} />
      <MonetizationGenderSection data={data} />
    </>
  );
};

export default DiscoveryAnalyses;
